import {get, extend, isString, isFunction, isEmpty, template} from 'lodash';
import React, {useState, useCallback, useContext} from 'react';
import {toast} from 'acud';

import {useLocale} from '../../../components/locale';
import {ConfigContext} from '../../../components/common/ConfigProvider';
import {createPayload, createParams} from '../../../components/biz/common/helper';
import {FETCH_TRIGGER_BY_RELOAD_TYPE} from './index';

const DIALOG_TYPES = ['dialog', 'drawer'];

const useCommand = (props: any) => {
    const {actions, history, selectedRow, tableActions} = props;

    const {selectedRowKeys, selectedItems} = selectedRow;

    const [dialogProps, setDialogProps] = useState({});

    const [locale] = useLocale('useTable');

    const {ajaxActionErrorToast} = useContext(ConfigContext)?.table || {};

    const handleDialogClose = useCallback(() => {
        setDialogProps(prevDialogProps => ({...prevDialogProps, visible: false}));
    }, []);

    const handleDialogConfirmLoading = useCallback((confirmLoadingText?: string | React.ReactNode) => {
        setDialogProps(prevDialogProps => ({
            ...prevDialogProps,
            confirmLoading: true,
            confirmLoadingText
        }));
    }, []);

    const handleDialogCancelConfirmLoading = useCallback(() => {
        setDialogProps(prevDialogProps => ({...prevDialogProps, confirmLoading: false}));
    }, []);

    const handleDialogAlertCommand = useCallback(({config, payload}) => {
        const {title, body: {content}, ...dialogProps} = config;
        setDialogProps({
            type: 'info',
            title,
            onConfirmLoading: handleDialogConfirmLoading,
            onCancelConfirmLoading: handleDialogCancelConfirmLoading,
            ...dialogProps,
            // 不支持覆盖的字段
            dialogType: 'dialog-box',
            content,
            payload,
            handleDialogClose
        });
    }, [handleDialogClose, handleDialogConfirmLoading, handleDialogCancelConfirmLoading]);

    const handleDialogOtherCommand = useCallback(({config, payload = {}, actionType}) => {
        const {title, body, ...dialogProps} = config;
        const {type, content, $payloadFields, $extraPayload, $onBeforeOpen} = body;
        payload = createPayload(payload, $payloadFields, $extraPayload);
        payload = typeof $onBeforeOpen === 'function'
            ? ($onBeforeOpen(payload) || payload) : payload;

        setDialogProps({
            title,
            onConfirmLoading: handleDialogConfirmLoading,
            onCancelConfirmLoading: handleDialogCancelConfirmLoading,
            onSuccess: () => tableActions?.loadList({triggerByType: FETCH_TRIGGER_BY_RELOAD_TYPE.Auto}),
            ...dialogProps,
            // 不支持覆盖的字段
            dialogType: actionType === 'drawer' ? actionType : 'modal',
            dialogSubType: type,
            content,
            payload,
            handleDialogClose
        });
    }, [tableActions, handleDialogClose, handleDialogConfirmLoading, handleDialogCancelConfirmLoading]);

    /**
     * 处理ajax类型的command
     * @param {number} num
     * @return {void}
     */
    const handleAjaxCommand = useCallback(({config, payload = {}}) => {
        const {
            apiAction,
            $payloadFields,
            $extraPayload,
            confirmText,
            confirmTitle = locale?.confirm + '?',
            errorToast = ajaxActionErrorToast ?? true,
            $toastMessage,
            $onRequest,
            $onResponse,
            $onError,
            ...dialogProps
        } = config;

        const initialPayload = createPayload(payload, $payloadFields, $extraPayload);
        // 向前兼容：$onRequest 返回数组时，视为 [payload, options]（options 可携带 headers）
        // 展开后传给 action，等价于 action(payload, {headers})；返回非数组或无返回时行为不变
        const onRequestResult = isFunction($onRequest)
            ? ($onRequest(initialPayload) || initialPayload)
            : initialPayload;
        const actionArgs = Array.isArray(onRequestResult)
            ? onRequestResult
            : [onRequestResult];
        const requestPayload = actionArgs[0];

        const withoutComfirm = isEmpty(confirmText);

        const send = () => {
            const action = isString(apiAction) ? get(actions, apiAction) : apiAction;
            if (!withoutComfirm && action) {
                handleDialogConfirmLoading();
            }
            return action(...actionArgs).then((response: any) => {
                if ($toastMessage) {
                    toast.success({
                        message: $toastMessage,
                        duration: 5
                    });
                }
                isFunction($onResponse) && $onResponse(response, requestPayload);
                !withoutComfirm && setDialogProps(prevDialogProps => ({
                    ...prevDialogProps,
                    confirmLoading: false,
                    visible: false
                }));
                // 刷新列表
                return tableActions?.loadList?.({triggerByType: FETCH_TRIGGER_BY_RELOAD_TYPE.Auto})
                || Promise.resolve();
            }).catch((error: any) => {
                isFunction($onError) && $onError(error, requestPayload);
                errorToast && toast.error({
                    message: error.global || locale?.requestFailedPleaseCheck,
                    duration: 5
                });
                handleDialogCancelConfirmLoading();
            });
        };

        if (withoutComfirm) {
            return send();
        }

        const title = isString(confirmTitle) ? template(confirmTitle)(payload) : confirmTitle;
        const content = isString(confirmText) ? template(confirmText)(payload) : confirmText;

        setDialogProps({
            type: 'warning',
            title,
            content,
            onOk: send,
            ...dialogProps,
            // 不支持覆盖的字段
            dialogType: 'dialog-box',
            handleDialogClose
        });

    }, [
        actions,
        tableActions,
        locale,
        ajaxActionErrorToast,
        handleDialogClose,
        handleDialogConfirmLoading,
        handleDialogCancelConfirmLoading
    ]);

    const handleCustomCommand = useCallback(({actionName, payload = {}}) => {
        // 特殊处理，刷新列表按钮
        if (actionName === 'loadList') {
            // 所有筛选条件不变，刷新列表
            tableActions.loadList?.();
        }
        // 特殊处理，全选列表选项
        else if (actionName === 'checkAll') {
            tableActions.checkAll?.(payload.checked);
        }
        else if (!isEmpty(tableActions)) {
            tableActions[actionName]?.(payload);
        }
    }, [tableActions]);

    const handleCommand = useCallback(({config, payload}) => {
        const {actionType, select} = config;
        select && (config.$extraPayload = {selectedItems, selectedRowKeys});
        const mergePayload = extend({}, payload, config.$extraPayload);

        if (actionType === 'link') {
            const {$payloadFields, $extraPayload} = config;
            const params = createParams(payload, $payloadFields, $extraPayload);
            const [path, search] = config.link?.split('?');
            const link = path + (params ? `/${params}` : '') + (search ? `?${search}` : '');
            if (history) {
                history.push ? history.push(link) : history(link);
            }
        }
        else if (DIALOG_TYPES.includes(actionType)) {
            const actionConfig = config[actionType];
            const {title, body} = actionConfig;
            const {type, content} = body;

            // 统一处理title和content
            const $title = isString(title) ? template(title)(payload) : title;
            const $content = isString(content) ? template(content)(payload) : content;
            actionConfig.title = $title;
            actionConfig.body.content = $content;
            if (actionType === 'dialog' && type === 'alert') {
                handleDialogAlertCommand({config: actionConfig, payload: mergePayload});
            }
            else if (actionType === 'drawer' || ['plain', 'common'].includes(type)) {
                handleDialogOtherCommand({config: actionConfig, payload: mergePayload, actionType});
            }
        }
        else if (actionType === 'ajax') {
            handleAjaxCommand({config: config.ajax, payload: mergePayload, tableActions});
        }
        else if (actionType === 'custom') {
            handleCustomCommand({actionName: config.action, payload: mergePayload, tableActions});
        }
    }, [
        history,
        tableActions,
        selectedItems,
        selectedRowKeys,
        handleDialogAlertCommand,
        handleDialogOtherCommand,
        handleAjaxCommand,
        handleCustomCommand
    ]);

    return {dialogProps, handleCommand};
};

export default useCommand;
